import { forwardRef, useCallback, useId } from 'react';
import { IonDatetimeButton } from '@ionic/react';
import { isEqual, noop } from 'lodash';
import { useContextSelector } from 'use-context-selector';
import moment from 'moment';

import { BaseFieldProps } from '../Fields.types';
import { LocalizationContext } from '../../../../src/contexts/LocalizationContext';
import { FieldWrapper } from '../FieldWrapper';

import {
  BLOCKS,
  MAX_DATE,
  MIN_DATE,
  VALUE_DATE_FORMAT,
} from './FieldDatePicker.const';
import { FieldDatePickerContext } from './FieldDatePicker.context';
import { FieldDatePickerMasked } from './FieldDatePickerMasked';

import styles from '../Fields.module.scss';

export interface FieldDatePickerProps extends BaseFieldProps<string> {
  min?: string;
  max?: string;
}

export const FieldDatePicker = forwardRef<
  HTMLIonInputElement,
  FieldDatePickerProps
>(
  (
    {
      value,
      onChange = noop,
      min = MIN_DATE,
      max = MAX_DATE,
      placeholder,
      error,
      onBlur = noop,
      enterKeyHint,
      label,
      disabled,
      onFocus,
      onKeyUp,
      onKeyDown,
      onCompositionUpdate,
    },
    ref
  ) => {
    const datetimeId = useId();

    const invalid = !!error;

    const format = useContextSelector(
      LocalizationContext,
      ({
        form: {
          datepicker: { format },
        },
      }) => format
    );

    const handleOnFormat = useCallback(
      (date: string) => {
        return moment(date).format(format);
      },
      [format]
    );

    const handleOnParse = useCallback(
      (str: string) => {
        return moment(str, format);
      },
      [format]
    );

    const handleOnAccept = useCallback(
      (acceptValue: string) => {
        const isValid = moment(acceptValue, format, true).isValid();
        const formatted = moment(acceptValue).format(VALUE_DATE_FORMAT);
        const notEqual = !isEqual(formatted, value);
        if (isValid && notEqual)
          onChange({
            value: formatted,
          });
      },
      [format, value, onChange]
    );

    return (
      <FieldDatePickerContext.Provider
        value={{
          datetimeId,
          error,
          invalid,
          max,
          min,
          onBlur,
          onChange,
          placeholder,
          value,
          enterKeyHint,
          disabled,
          onFocus,
          onKeyUp,
          onKeyDown,
          onCompositionUpdate,
        }}
      >
        <FieldWrapper label={label} invalid={invalid}>
          <FieldDatePickerMasked
            mask={Date}
            blocks={BLOCKS}
            pattern={format}
            parse={handleOnParse}
            format={handleOnFormat}
            onAccept={handleOnAccept}
            ref={(el) => {
              // @ts-ignore
              if (ref) ref.current = el?.element;
            }}
            value={value && moment(value, VALUE_DATE_FORMAT).format(format)}
          />
          <IonDatetimeButton
            slot="end"
            datetime={datetimeId}
            className={styles['date-picker-datetime-button']}
            defaultValue={value}
          />
        </FieldWrapper>
      </FieldDatePickerContext.Provider>
    );
  }
);
